import { useState, useCallback, useEffect, useContext } from 'react';

import AppsRoundedIcon from '@mui/icons-material/AppsRounded';
import CloseIcon from '@mui/icons-material/CloseRounded';
import ContentCopyRoundedIcon from '@mui/icons-material/ContentCopyRounded';
import DeleteRoundedIcon from '@mui/icons-material/DeleteRounded';
import EditRoundedIcon from '@mui/icons-material/EditRounded';
import FitScreenRoundedIcon from '@mui/icons-material/FitScreenRounded';
import HighlightAltRoundedIcon from '@mui/icons-material/HighlightAltRounded';
import ImportExportRoundedIcon from '@mui/icons-material/ImportExportRounded';
import ListRoundedIcon from '@mui/icons-material/ListRounded';
import LockOpenRoundedIcon from '@mui/icons-material/LockOpenRounded';
import LockRoundedIcon from '@mui/icons-material/LockRounded';
import MapRoundedIcon from '@mui/icons-material/MapRounded';
import HandIcon from '@mui/icons-material/PanToolRounded';
import SaveIcon from '@mui/icons-material/SaveRounded';
import ZoomInRoundedIcon from '@mui/icons-material/ZoomInRounded';
import ZoomOutRoundedIcon from '@mui/icons-material/ZoomOutRounded';
import {
  Box,
  Button,
  IconButton,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  TextField,
  useTheme,
} from '@mui/material';
import { trackAnalyticsEvent } from '@ragu-components/ord-analytics';
import { useReactFlow, useNodes, useStore, useStoreApi } from '@xyflow/react';
import _ from 'lodash';
import { EditBase, useUpdate, useRecordContext, useNotify, useTranslate } from 'react-admin';
import { v4 as uuidv4 } from 'uuid';

import { formIsDirtyContext } from 'src/context';
import { flowchartLogger } from 'src/lib/logger';

import ButtonDownload from './components/ButtonDownload';
import ButtonUpload from './components/ButtonUpload';
import MenuDropdown from './components/MenuDropdown';
import { mouseSelectionContext } from './context/mouseSelectionContext';
import FrontendData from './dataProvider/prepareFrontendData';
import useBackendData from './dataProvider/useBackendData';
import useAutoLayout from './hooks/useAutoLayout';
import { useCenterOnNode } from './hooks/useCenterOnNode';
import { isHttp402Error, useServerValidationErrors } from './hooks/useServerValidationErrors';

import type { DialplanDataBase, DialplanDetailedDataBase, NodeMenuConfig } from './dataProvider/types';
import type { Node, Edge, ReactFlowJsonObject, ReactFlowState } from '@xyflow/react';

export const createNodeEdgeCopy = (selectedNodes: Node[], edges: Edge[]): { nodes: Node[]; edges: Edge[] } => {
  // get nodes
  flowchartLogger.debug('Handle copy. Selected nodes:', selectedNodes);
  flowchartLogger.debug('Handle copy. Edges:', edges);

  // CheckRounded if there are nodes selected
  if (selectedNodes.length !== 0) {
    const duplicateEdges: Edge[] = [];
    const clonedNodesIds: string[] = [];

    const duplicateNodes = selectedNodes.map((node) => {
      const cloneNode = _.cloneDeep(node);
      const cloneNodeId = uuidv4();
      const oldNodeId = cloneNode.id;
      cloneNode.id = cloneNodeId;
      cloneNode.position = { x: node.position.x + 10, y: node.position.y + 10 };

      const foundDuplicateEdges = [];
      for (const edge of duplicateEdges) {
        if (edge.source === oldNodeId || edge.target === oldNodeId) {
          foundDuplicateEdges.push(edge.id);
          if (edge.source === oldNodeId) {
            edge.source = cloneNodeId;
          } else {
            edge.target = cloneNodeId;
          }
        }
      }
      for (const edge of edges) {
        if (
          foundDuplicateEdges.find((duplicateId) => duplicateId === edge.id) === undefined &&
          (edge.source === oldNodeId || edge.target === oldNodeId)
        ) {
          const cloneEdge = _.cloneDeep(edge);
          if (edge.source === oldNodeId) {
            cloneEdge.source = cloneNodeId;
          } else {
            cloneEdge.target = cloneNodeId;
          }
          duplicateEdges.push(cloneEdge);
        }
      }
      clonedNodesIds.push(cloneNodeId);

      return cloneNode;
    });

    duplicateEdges.forEach((edge) => {
      edge.id = uuidv4();
    });
    flowchartLogger.debug('clonedNodesIds:', clonedNodesIds);
    const filteredEdges = duplicateEdges.filter((edge) => clonedNodesIds.includes(edge.source));
    return { nodes: duplicateNodes, edges: filteredEdges };
  }
  return { nodes: [], edges: [] };
};

type DialogType = 'edit' | 'import-export' | '';

interface DialogProps {
  handleDialogClose: () => void;
}

interface ImportExportDialogProps extends DialogProps {
  onUpdateFlow: () => void;
  saveState: string;
  setSaveState: (value: string) => void;
}

const ImportExportDialog = ({
  handleDialogClose,
  onUpdateFlow,
  saveState,
  setSaveState,
}: ImportExportDialogProps): React.ReactElement => {
  const translate = useTranslate();
  return (
    <>
      <DialogTitle>{translate('callflows.interface.importExport')}</DialogTitle>
      <DialogContent>
        <DialogContentText>{translate('callflows.interface.importExportDescription')}</DialogContentText>
        <TextField
          autoFocus
          label={translate('callflows.interface.backupData')}
          multiline
          rows={10}
          fullWidth
          variant='standard'
          value={saveState}
          onChange={(event) => {
            setSaveState(event.target.value);
          }}
        />
      </DialogContent>
      <DialogActions>
        <ButtonUpload setValueOnUpload={setSaveState} />
        <ButtonDownload saveData={saveState} />
        <Divider orientation='vertical' flexItem />
        <Button onClick={handleDialogClose} color='error'>
          {translate('ra.action.cancel')}
        </Button>
        <Button onClick={onUpdateFlow} variant='contained' color='primary'>
          {translate('ra.action.update')}
        </Button>
      </DialogActions>
    </>
  );
};

interface EditDialogProps extends DialogProps {
  id: string;
  data: DialplanDataBase;
  children: React.ReactNode;
  resource: string;
}

const EditDialog = ({ handleDialogClose, id, data, children, resource }: EditDialogProps): React.ReactElement => {
  const translate = useTranslate();

  return (
    <>
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        {translate('callflows.interface.editAndSave')}
        <IconButton onClick={handleDialogClose} size='small'>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <EditBase
          resource={resource}
          redirect={false}
          id={id}
          transform={(dataArg) => {
            const dataClone = Object.assign({}, dataArg);
            const patchData = {
              ...dataClone,
              ...data,
            };
            delete patchData.id;
            delete patchData.entry;
            delete patchData.nodes;
            delete patchData.is_backup;
            delete patchData.last_edit;
            // Have to delete all the version fields manually instead of using backupVersionField
            // This is to prevent eslint errors. In addition, delete doesn't throw errors on missing keys.
            delete patchData.latest_callflow_version;
            return patchData;
          }}
        >
          {children}
        </EditBase>
      </DialogContent>
    </>
  );
};

const selector = (s: ReactFlowState): { isInteractive: boolean; minZoomReached: boolean; maxZoomReached: boolean } => ({
  isInteractive: s.nodesDraggable || s.nodesConnectable || s.elementsSelectable,
  minZoomReached: s.transform[2] <= s.minZoom,
  maxZoomReached: s.transform[2] >= s.maxZoom,
});

interface MenuPanelProps {
  resource: string;
  recordChanged: boolean;
  saveAlwaysEnabled: boolean;
  backupVersionField: string | undefined;
  nodeMenuConfig: NodeMenuConfig;
  setRecordChanged: (value: boolean) => void;
  minimapVisible: boolean;
  toggleMinimap: () => void;
  toggleListVisibility: () => void;
  editForm: React.ReactNode;
  /** Disables all editing actions (readonly show route) */
  isReadonly: boolean;
}

const MenuPanel = ({
  resource,
  recordChanged,
  saveAlwaysEnabled,
  backupVersionField,
  nodeMenuConfig,
  setRecordChanged,
  minimapVisible: _minimapVisible,
  toggleMinimap,
  toggleListVisibility,
  editForm,
  isReadonly,
}: MenuPanelProps): React.ReactElement => {
  const [dialogType, setDialogType] = useState<DialogType>('');
  const [saveState, setSaveState] = useState('');
  const [formIsDirty, setFormIsDirty] = useState(false);

  const nodes = useNodes();
  const hasSelectedNodes = nodes.some((node) => node.selected === true);

  useEffect(() => {
    flowchartLogger.debug('Found selected nodes:', hasSelectedNodes);
  }, [hasSelectedNodes]);

  const { useMouseSelection, setUseMouseSelection } = useContext(mouseSelectionContext);
  const [update, { isLoading }] = useUpdate();
  const flowchartRecord = useRecordContext();
  const notify = useNotify();
  const reactFlowInstance = useReactFlow();
  const { clearServerValidation, applyServerValidationErrors } = useServerValidationErrors(reactFlowInstance);
  const { isInteractive, minZoomReached, maxZoomReached } = useStore(selector);
  const store = useStoreApi();
  const translate = useTranslate();
  const backendData = useBackendData();
  const centerOnNode = useCenterOnNode();
  const autoLayout = useAutoLayout();
  const theme = useTheme();

  const isBackup = flowchartRecord?.is_backup === true;
  const isEditingDisabled = isReadonly || isBackup;

  useEffect(() => {
    if (!saveAlwaysEnabled) {
      setRecordChanged(false);
    }
  }, [flowchartRecord, setRecordChanged, saveAlwaysEnabled]);

  // Toggle interactivity for the record
  useEffect(() => {
    const isInteractive = !isEditingDisabled;
    store.setState({
      nodesDraggable: isInteractive,
      nodesConnectable: isInteractive,
      nodesFocusable: isInteractive,
      elementsSelectable: isInteractive,
    });
  }, [isEditingDisabled, store]);

  const handleSave = (): void => {
    if (flowchartRecord === undefined) return;
    clearServerValidation();
    let shouldRegenerateIds = false;
    let saveId = flowchartRecord.id;
    if (backupVersionField !== undefined && flowchartRecord.is_backup === true) {
      shouldRegenerateIds = true;
      // Parsing keys that has nested id, such as 'latest_callflow_version.id'
      if (backupVersionField.endsWith('.id')) {
        saveId = flowchartRecord[backupVersionField.slice(0, -3)].id;
      } else {
        saveId = flowchartRecord[backupVersionField];
      }
      flowchartLogger.debug('Saving backup record with id:', saveId);
    }

    const data = backendData(
      { nodes: reactFlowInstance.getNodes(), edges: reactFlowInstance.getEdges() },
      resource,
      shouldRegenerateIds,
      centerOnNode
    );
    flowchartLogger.info('Saving record');
    flowchartLogger.debug('Saving record with data:', data);

    if (data !== false) {
      void update(
        resource,
        {
          id: saveId,
          data,
          previousData: flowchartRecord,
        },
        {
          onSuccess: () => {
            flowchartLogger.debug('Successfully saved record');
            trackAnalyticsEvent('callflow_saved', {
              event_category: 'callflow',
              content_type: resource,
              method: shouldRegenerateIds ? 'backup_restore' : 'save',
            });
            if (shouldRegenerateIds) {
              notify('callflows.notifications.restored', { undoable: false });
            } else {
              notify('callflows.notifications.updated', { undoable: false });
            }
            // Reset the changes after save
            if (!saveAlwaysEnabled) {
              setRecordChanged(false);
            }
            localStorage.removeItem(`flowData-${saveId}`);
          },
          onError: (error) => {
            flowchartLogger.error('Failed to save record');
            trackAnalyticsEvent('callflow_save_failed', { event_category: 'callflow', content_type: resource });
            if (isHttp402Error(error)) {
              notify('ord.httpErrors.402', { type: 'error' });
              return;
            }
            applyServerValidationErrors(error, data);
            notify('callflows.notifications.errorSaving', { type: 'error' });
          },
        }
      );
    }
  };

  const handleMouseSelection = useCallback(() => {
    const newUseMouseSelection = !useMouseSelection;
    setUseMouseSelection(newUseMouseSelection);
    localStorage.setItem('flowchartUseMouseSelection', newUseMouseSelection.toString());
    flowchartLogger.debug('Mouse selection mode:', newUseMouseSelection);
  }, [useMouseSelection, setUseMouseSelection]);

  const deselectAll = useCallback(() => {
    flowchartLogger.debug('Deselecting all nodes');
    reactFlowInstance.setNodes((nodes) =>
      nodes.map((node) => {
        node.selected = false;
        return node;
      })
    );
  }, [reactFlowInstance]);

  const handleNodeCopy = useCallback(() => {
    // get nodes
    const selectedNodes = reactFlowInstance.getNodes().filter((node) => node.selected);
    flowchartLogger.debug('Handle copy. Selected nodes:', selectedNodes);

    const edges = reactFlowInstance.getEdges();
    flowchartLogger.debug('Handle copy. Edges:', edges);

    // CheckRounded if there are nodes selected
    if (selectedNodes.length !== 0) {
      const { nodes: duplicateNodes, edges: filteredEdges } = createNodeEdgeCopy(selectedNodes, edges);

      trackAnalyticsEvent('callflow_nodes_duplicated', { event_category: 'callflow', value: duplicateNodes.length });
      deselectAll();
      flowchartLogger.debug('Duplicating nodes:', duplicateNodes);
      reactFlowInstance.setNodes((nodes) => nodes.concat(...duplicateNodes));
      flowchartLogger.debug('Duplicating edges:', filteredEdges);
      reactFlowInstance.setEdges((edges) => edges.concat(...filteredEdges));
    }
  }, [deselectAll, reactFlowInstance]);

  const handleNodeDelete = useCallback(() => {
    // get nodes
    const selectedNodes = reactFlowInstance.getNodes().filter((node) => node.selected);
    flowchartLogger.debug('Handle delete. Selected nodes:', selectedNodes);

    if (selectedNodes.length !== 0) {
      trackAnalyticsEvent('callflow_nodes_deleted', { event_category: 'callflow', value: selectedNodes.length });
      void reactFlowInstance.deleteElements({ nodes: selectedNodes });
    }
  }, [reactFlowInstance]);

  const handleDialogOpen = (dialogType: DialogType): void => {
    const flow: ReactFlowJsonObject = reactFlowInstance.toObject();
    const data = backendData({ nodes: flow.nodes, edges: flow.edges }, undefined, undefined, centerOnNode);
    flowchartLogger.debug(`Opening dialog ${dialogType} with data:`, data);

    // Don't open edit dialog if data is incorrect
    if (data === false) {
      return;
    }
    setSaveState(JSON.stringify(data));
    setDialogType(dialogType);
  };

  const handleDialogClose = (): void => {
    setDialogType('');
  };

  const onUpdateFlow = useCallback(() => {
    const updateFlow = (): void => {
      const frontendData = new FrontendData(JSON.parse(saveState) as DialplanDetailedDataBase);
      const { nodes, edges } = frontendData.prepare();
      flowchartLogger.debug('Updating flow with nodes:', nodes);
      flowchartLogger.debug('Updating flow with edges:', edges);

      trackAnalyticsEvent('callflow_imported', { event_category: 'callflow', value: nodes.length });
      reactFlowInstance.setNodes(nodes);
      reactFlowInstance.setEdges(edges);
      handleDialogClose();
    };

    updateFlow();
  }, [reactFlowInstance, saveState]);

  const onToggleInteractivity = (): void => {
    store.setState({
      nodesDraggable: !isInteractive,
      nodesConnectable: !isInteractive,
      nodesFocusable: !isInteractive,
      elementsSelectable: !isInteractive,
    });
  };
  if (flowchartRecord === undefined) return <></>;
  return (
    <Box display='flex' flexWrap='wrap' gap={4} sx={{ px: 4 }}>
      {!isEditingDisabled && (
        <Box sx={{ display: 'flex', gap: 2, paddingRight: 2 }}>
          {nodeMenuConfig.map((item) => (
            <MenuDropdown key={item.label} labelBefore={<item.icon />} label={item.label} menuItems={item.items} />
          ))}
        </Box>
      )}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          width: 'fit-content',
          padding: theme.spacing(1, 2),
          borderRadius: 1,
          bgcolor: 'background.default',
          color: 'text.secondary',
          gap: 2.25,
          '& hr': {
            mx: -1.125,
          },
          '@media(min-width: 600px)': {
            minHeight: '48px',
          },
        }}
      >
        {useMouseSelection ? (
          <IconButton
            size='small'
            onClick={handleMouseSelection}
            title={translate('callflows.interface.selectionModeEnabled')}
          >
            <HighlightAltRoundedIcon />
          </IconButton>
        ) : (
          <IconButton
            size='small'
            onClick={handleMouseSelection}
            title={translate('callflows.interface.panningModeEnabled')}
          >
            <HandIcon />
          </IconButton>
        )}
        <IconButton
          size='small'
          onClick={() => {
            void reactFlowInstance.zoomOut();
          }}
          disabled={minZoomReached}
          title={translate('callflows.interface.zoomOut')}
        >
          <ZoomOutRoundedIcon />
        </IconButton>
        <IconButton
          size='small'
          onClick={() => {
            void reactFlowInstance.zoomIn();
          }}
          disabled={maxZoomReached}
          title={translate('callflows.interface.zoomIn')}
        >
          <ZoomInRoundedIcon />
        </IconButton>
        <IconButton
          size='small'
          onClick={() => {
            void reactFlowInstance.fitView();
          }}
          title={translate('callflows.interface.fitView')}
        >
          <FitScreenRoundedIcon />
        </IconButton>
        {!isEditingDisabled && (
          <IconButton
            size='small'
            onClick={() => {
              autoLayout();
              setRecordChanged(true);
            }}
            title={translate('callflows.interface.autoPositionNodes')}
          >
            <AppsRoundedIcon />
          </IconButton>
        )}
        <IconButton
          size='small'
          onClick={onToggleInteractivity}
          title={translate('callflows.interface.toggleInteractivity')}
          disabled={isEditingDisabled}
        >
          {isInteractive ? <LockOpenRoundedIcon /> : <LockRoundedIcon />}
        </IconButton>
        {!isEditingDisabled && (
          <>
            <Divider orientation='vertical' flexItem />
            <IconButton
              size='small'
              disabled={!hasSelectedNodes}
              onClick={handleNodeCopy}
              title={translate('callflows.interface.copyNode')}
            >
              <ContentCopyRoundedIcon />
            </IconButton>
            <IconButton
              size='small'
              disabled={!hasSelectedNodes}
              onClick={handleNodeDelete}
              title={translate('callflows.interface.deleteSelected')}
            >
              <DeleteRoundedIcon />
            </IconButton>
            <Divider orientation='vertical' flexItem />
            <IconButton
              size='small'
              onClick={() => {
                handleDialogOpen('edit');
              }}
              disabled={isLoading}
              title={translate('callflows.interface.editAndSave')}
            >
              <EditRoundedIcon />
            </IconButton>
            <IconButton
              size='small'
              title={translate('ra.action.save')}
              disabled={isLoading || (!saveAlwaysEnabled && !recordChanged)}
              onClick={handleSave}
            >
              <SaveIcon />
            </IconButton>
            <IconButton
              size='small'
              onClick={() => {
                handleDialogOpen('import-export');
              }}
              disabled={isLoading}
              title={translate('callflows.interface.importExport')}
            >
              <ImportExportRoundedIcon />
            </IconButton>
          </>
        )}
        {isBackup && !isReadonly && (
          <>
            <Divider orientation='vertical' flexItem />
            <Button
              size='small'
              title={translate('ra.action.save')}
              color='inherit'
              disabled={isLoading}
              onClick={handleSave}
            >
              {translate('callflows.interface.restoreBackup')}
            </Button>
          </>
        )}
        <Divider orientation='vertical' flexItem />
        <IconButton
          size='small'
          onClick={toggleListVisibility}
          title={translate('callflows.interface.toggleListVisibility')}
        >
          <ListRoundedIcon />
        </IconButton>
        <IconButton size='small' onClick={toggleMinimap} title={translate('callflows.interface.toggleMiniMap')}>
          <MapRoundedIcon />
        </IconButton>
      </Box>
      <Dialog
        open={dialogType !== ''}
        onClose={formIsDirty ? undefined : handleDialogClose}
        slotProps={{ paper: { sx: { width: 'calc(100% - 64px)', maxWidth: '700px' } } }}
      >
        <formIsDirtyContext.Provider value={{ formIsDirty, setFormIsDirty }}>
          {dialogType === 'edit' && (
            <EditDialog
              resource={resource}
              handleDialogClose={handleDialogClose}
              id={flowchartRecord.id.toString()}
              data={JSON.parse(saveState)}
            >
              {editForm}
            </EditDialog>
          )}

          {dialogType === 'import-export' && (
            <ImportExportDialog
              handleDialogClose={handleDialogClose}
              saveState={saveState}
              setSaveState={setSaveState}
              onUpdateFlow={onUpdateFlow}
            />
          )}
        </formIsDirtyContext.Provider>
      </Dialog>
    </Box>
  );
};

export default MenuPanel;
